import { useRef } from 'react'
import cn from 'classnames'
import Section from '../Section'
import Slider from '../Slider'
import ResponsiveImage from '../ResponsiveImage'
import RichContent from '../RichContent'
import StyledLink from '../StyledLink'
import { createUseStyles } from '../../helpers/createStyles'
import { theme, grid, span } from '../../styles/theme'
import useSnapshot from '../../store/useSnapshot'
import { getDesignConsultationForm } from '../../store/layoutSlice'
import useScrollTrigger from '../../hooks/useScrollTrigger'
import isEmpty from 'lodash/isEmpty'

const sliderOptions = {
  cellAlign: 'left'
}

export default function ContactSlice ({ data }) {
  const { title, images, locations } = data

  const styles = useStyles()
  const snap = useSnapshot()
  const designConsultationForm = getDesignConsultationForm(snap)
  const locationGridRef = useRef(null)

  const sectionRef = useScrollTrigger(
    () => ({
      trigger: locationGridRef.current,
      start: () => `${window.innerHeight / 4}px bottom`,
      scrub: false
    }),
    (tl, ref) => {
      const defaults = {
        ease: 'power2.inOut',
        duration: 0.8
      }
      tl.from(ref.current.querySelectorAll(`.${styles.textCol} h2, .${styles.location}`), { y: 40, opacity: 0, stagger: 0.1, ...defaults })
    }
  )

  const hasImages = !isEmpty(images)

  return (
    <Section className={styles.contactSlice} grid ref={sectionRef}>
      <div className={styles.textCol}>
        <h2>{title}</h2>
      </div>
      {hasImages && (
        <div className={styles.sliderCol}>
          <Slider options={sliderOptions} className={styles.slider}>
            {images?.map(image => (
              <div key={image._key} className={styles.sliderImageWrap}>
                <ResponsiveImage
                  className={styles.sliderImage}
                  image={image}
                />
              </div>
            ))}
          </Slider>
        </div>
      )}
      <div className={cn(styles.locationsContainer, styles.locationsGrid)} ref={locationGridRef}>
        {locations.map(location => (
          <div key={location._key} className={styles.location}>
            <span className={styles.locationTitle}>{location.title}</span>
            <RichContent className={styles.locationContent} content={location.content} />
            {location?.link && (
              <StyledLink className={cn(styles.consultButton, 'styled-link')} link={location.link} />
            )}
            {!location?.link && (
              <StyledLink
                className={cn(styles.consultButton, 'styled-link')}
                link={designConsultationForm}
                showText={false}
              >
                Book Styling Service
              </StyledLink>
            )}
          </div>
        ))}
      </div>
    </Section>
  )
}

const useStyles = createUseStyles({
  contactSlice: {
    rowGap: `${theme.spacingPx(4)} !important`,
    [theme.breakpoints.up('md')]: {
      rowGap: `${theme.spacingPx(7)} !important`
    }
  },
  textCol: {
    gridColumn: '1 / -1',
    gridRow: 1,
    [theme.breakpoints.up('md')]: {
      gridRow: 2,
      gridColumn: '1 / span 4',
      '& h2': {
        maxWidth: '7.2em'
      }
    }
  },
  sliderCol: {
    gridColumn: '1 / -1',
    grirdRow: 1,
    overflow: 'hidden',
    margin: 0,
    paddingLeft: `${theme.gutter.sm}px`,
    paddingRight: `${theme.gutter.sm}px`,
    marginLeft: `-${theme.gutter.sm}px`,
    marginRight: `-${theme.gutter.sm}px`,
    zIndex: 5,
    [theme.breakpoints.up('md')]: {
      paddingLeft: `${theme.gutter.md}px`,
      paddingRight: `${theme.gutter.md}px`,
      marginLeft: `-${theme.gutter.md}px`,
      marginRight: `-${theme.gutter.md}px`,
      marginTop: 6
    }
  },
  sliderImageWrap: {
    width: span(6, 'md'),
    marginRight: theme.gutter.md,
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      width: `calc(100% - ${theme.gutter.sm}px)`,
      marginRight: theme.gutter.sm
    }
  },
  sliderImage: {
    width: '100%'
  },
  locationsContainer: {
    gridColumn: '1 / -1',
    [theme.breakpoints.up('md')]: {
      gridColumn: '5 / span 6',
      gridRow: 2
    }
  },
  locationsGrid: {
    display: 'grid',
    gridTemplateColumns: `repeat(${grid.sm.columns}, minmax(0px, 1fr))`,
    gap: `${grid.sm.gutter}px`,
    [theme.breakpoints.up('md')]: {
      gap: `${grid.md.gutter}px`,
      gridTemplateColumns: 'repeat(6, minmax(0px, 1fr))'
    }
  },
  location: {
    gridColumn: 'span 8',
    marginBottom: theme.spacingPx(4),
    [theme.breakpoints.up('md')]: {
      gridColumn: 'span 3',
      marginBottom: theme.spacingPx(8)
    }
  },
  locationTitle: {
    display: 'block',
    fontFamily: theme.fonts.body,
    fontWeight: theme.fonts.bodyFontWeight,
    fontSize: '12px',
    lineHeight: '14px',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    paddingBottom: theme.spacingPx(1.5),
    marginBottom: theme.spacingPx(3),
    position: 'relative',
    '&:after': {
      content: '""',
      position: 'absolute',
      width: '100%',
      height: '1px',
      backgroundColor: 'currentColor',
      top: '100%',
      left: '0',
      pointerEvents: 'none'
    }
  },
  locationContent: {},
  consultButton: {
    marginTop: theme.spacingPx(4),
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacingPx(6)
    }
  }
})
